// @flow

import React from 'react';

import * as Sentry from '@sentry/browser';

import config from '../configs/envConfig';
import openUrls from './openUrls';
import { zoomConstants } from './apiService/apiEndPoints';
import { IncompleteCnConstants } from '../components/helper/Constants';

const createHistory = require('history').createBrowserHistory;

export function enableSentry() {
  if (process.env.NODE_ENV === 'prod') {
    Sentry.init({
      dsn: 'https://d695f2a67f244957a85641c51db14cb8@sentry.io/1437648'
    });
  }
}

export const paymentTypeMap = {
  CASH: { key: 'CASH', displayValue: 'Cash' },
  CHEQUE: { key: 'CHEQUE', displayValue: 'Cheque' },
  BANK_TRANSFER: { key: 'BANK_TRANSFER', displayValue: 'Bank Transfer' },
  ONLINE: { key: 'ONLINE', displayValue: 'Online' }
};

export const rivigoOrganizationId = 1;

export const rivigoRetailOrganizationId = 9999;

export const retlClientId = 51;

export const hqtrLocationId = 9;

export const clientPriorityObject = {
  label: 'P',
  text: 'Priority',
  backendMappingEnum: '',
  priority: 'P1'
};

export const clientContractStatus = {
  ACTIVE: 'ACTIVE',
  EXPIRED: 'EXPIRED'
};

export const toTitleCase = str => {
  if (str) {
    return str.replace(/\w\S*/g, function(txt) {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
  }
};

export const dateFormatter = (timestamp, dateOnly) => {
  if (timestamp) {
    let currDate = new Date(timestamp);
    let newDate = '';
    const date = currDate.getDate();
    const month = currDate.getMonth() + 1;
    const year = currDate.getFullYear();
    newDate = date + '-' + month + '-' + year;
    if (dateOnly) {
      return newDate;
    } else {
      let time = currDate.toLocaleString('en-GB', {
        hour: 'numeric',
        minute: '2-digit'
      });
      let dateWithTime = newDate + time;
      return dateWithTime;
    }
  }
};

export const isClient = (userProfile: Object) =>
  userProfile.authorities &&
  (userProfile.authorities.indexOf('ROLE_ZOOM_CLIENT_ADMIN') > -1 ||
    userProfile.authorities.indexOf('ROLE_ZOOM_CLIENT_CONSIGNOR') > -1 ||
    userProfile.authorities.indexOf('ROLE_ZOOM_CLIENT_CONSIGNEE') > -1);

// export const clientAppUuid = 'e3eede03-84e0-497e-a18f-668f7c09263e'; // for dagger
export const clientAppUuid = 'e3eede03-84e0-497e-a18f-668f7c09263d'; /// only for throne
export const bpAdminUser = (userProfile: Object) =>
  userProfile.authorities &&
  userProfile.authorities.indexOf('ROLE_ZOOM_BP') > -1;

export const bpCaptainUser = (userProfile: Object) =>
  userProfile.authorities &&
  userProfile.authorities.indexOf('ROLE_ZOOM_BP_USER') > -1;

export const techSupportUser = (userProfile: Object) =>
  userProfile.authorities &&
  userProfile.authorities.indexOf('ROLE_VIEW_ZOOM_TECH_SUPPORT_PANEL') > -1;

export const opcUser = (userProfile: Object) =>
  userProfile.authorities &&
  userProfile.authorities.indexOf('ROLE_VIEW_OPC_PANEL') > -1;

export const rivigoClient = (clientData: Object) =>
  clientData &&
  (clientData.organizationId === rivigoOrganizationId ||
    clientData.organizationId === rivigoRetailOrganizationId);

export const nonRivigoClient = (clientData: Object) =>
  clientData &&
  clientData.organizationId !== rivigoOrganizationId &&
  clientData.organizationId !== rivigoRetailOrganizationId;

export const retailTypeClient = (clientData: Object) =>
  clientData && clientData.cnoteType !== 'NORMAL';

export const retlClient = (clientData: Object) =>
  clientData && clientData.id === retlClientId;

export const rivigoUser = (userProfile: Object) =>
  userProfile && userProfile.organizationId === rivigoOrganizationId;

export const nonRivigoUser = (userProfile: Object) =>
  userProfile && userProfile.organizationId !== rivigoOrganizationId;

export const checkForClientUser = (userProfile: Object) =>
  userProfile.authorities &&
  userProfile.authorities.indexOf('ROLE_ZOOM_CLIENT') !== -1;

export const isEditAllowedForRetailCollections = (userProfile: Object) =>
  userProfile.authorities &&
  userProfile.authorities.indexOf('ROLE_EDIT_COLLECTIONS_SUMMARY') !== -1;

export const checkForBlockedRole = (userProfile: Object) =>
  userProfile.authorities &&
  userProfile.authorities.indexOf('ROLE_BLOCK_CLIENTS') !== -1;

export const checkForExternalClient = (userProfile: Object) =>
  userProfile.authorities &&
  userProfile.authorities.indexOf('ROLE_EXTERNAL_TRACKING') !== -1;

export const checkForAnonymousUser = (userProfile: Object) =>
  userProfile.authorities &&
  userProfile.authorities.indexOf('ROLE_ANONYMOUS_USER') !== -1;

export const checkForHqtrUser = (userProfile: Object) =>
  userProfile.authorities &&
  userProfile.authorities.indexOf('ROLE_ZOOM_HQTR') > -1 &&
  userProfile.location_id === hqtrLocationId;

export const checkForEretlClient = (userProfile: Object) =>
  userProfile && isClient(userProfile);

export const checkForNonEretlClient = (userProfile: Object) =>
  userProfile && userProfile.tenantId !== 'ERETL' && isClient(userProfile);

export const checkForDominoPrintechClient = (userProfile: Object) => {
  if (
    userProfile &&
    userProfile.sessionProperties &&
    userProfile.sessionProperties.DOMINO_CLIENT_CODES &&
    userProfile.tenantId
  ) {
    const DOMINO_CLIENT_CODES_LIST = userProfile.sessionProperties.DOMINO_CLIENT_CODES.split(
      ','
    );
    return DOMINO_CLIENT_CODES_LIST.indexOf(userProfile.tenantId) > -1;
  }
  return false;
};

export const history = createHistory({ basename: config.baseName });

export const convertToRegex = (regString: string) =>
  new RegExp(`^ ${regString} $`);

export const isArrayNonEmpty = (testArray: Array<any>) =>
  testArray && testArray.length > 0;

export const isStringNonEmpty = (testString: string) =>
  testString && testString.length > 0;

export const isEwayBillPaternValid = (testEwayBill: string) =>
  Number(testEwayBill) === 0 ||
  (Number(testEwayBill) > 0 &&
    Number(testEwayBill) % 1 === 0 &&
    testEwayBill.length === 12);

export const getSelectOptionList = (tableData?: Object, columnKey: string) => {
  const selectOptionListMap = {};
  const selectOptionList = [];

  tableData.forEach(row => {
    const columnVal = row[columnKey];
    if (!Object.prototype.hasOwnProperty.call(selectOptionListMap, columnVal)) {
      selectOptionListMap[columnVal] = columnVal;
      selectOptionList.push({ key: columnVal, val: columnVal });
    }
  });

  return selectOptionList;
};

export const filterForColumnSum = (row: Object, filterObject: Object) => {
  const filterArrayLength = filterObject.filterColummnKeys.length;

  for (let i = 0; i < filterArrayLength; i += 1) {
    if (
      row[filterObject.filterColummnKeys[i]].toLowerCase() !==
      filterObject.filterValues[i].toLowerCase()
    ) {
      return false;
    }
  }

  return true;
};

export const getColumnSum = (
  tableData: Object,
  columnKey: string,
  filterObject?: Object,
  filterFunction?: Function
) => {
  let columnSum = 0;

  if (tableData) {
    tableData.forEach(row => {
      if (
        filterObject === null ||
        (filterObject !== null && filterFunction
          ? filterFunction(row, filterObject)
          : filterForColumnSum(row, filterObject))
      ) {
        columnSum += row[columnKey];
      }
    });
  }

  return columnSum;
};

export const appUpdateWarnings = {
  message: 'App version is outdated. Please refresh to continue.',
  refreshRequiredConfirm: 'Refresh required: Confirmed',
  refreshRequiredCancel: 'Refresh required: Cancelled'
};

export const authTokenUpdateWarnings = {
  message: 'Your session has expired. Please refresh to continue.'
};

export const strUsables: Object = {
  emptySpace: ' ',
  eqString: '=',
  accessToken: 'access_token',
  bearerString: 'Bearer',
  commaString: ',',
  openBracket: '(',
  closeBracket: ')',
  indianCurrencySymol: '₹',
  CREATED: 'Created',
  COMPLETE: 'Complete',
  DELETED: 'Deleted',
  DRAFTED: 'Drafted',
  DELETED_BY_BP: 'Deleted',
  CLOSED: 'Closed',
  MISSED: 'Missed',
  UNASSIGNED: 'Unassigned',
  YET_TO_REACH: 'Yet to Reach',
  IN_PROGRESS: 'In Progress',
  UNSUCCESSFUL: 'Unsuccessful',
  INCOMPLETE: 'Incomplete',
  REACHED_AT_CLIENT_WAREHOUSE: 'Reached',
  APPROVAL_PENDING: 'Approval Pending',
  FAILURE: 'Failure'
};

export const formattedStatus = (enumstr: string) => strUsables[enumstr];

export const roundOff = (num, point) =>
  Number(typeof point === 'number' ? num.toFixed(point) : num.toFixed(2));

export const getFormattedDate = (epochTime: numeric) =>
  epochTime
    ? new Date(epochTime).toLocaleDateString('en-US', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      })
    : '';

export const getFormattedTime = (epochTime: numeric) =>
  epochTime
    ? new Date(epochTime).toLocaleString('en-US', {
        hour: 'numeric',
        minute: 'numeric',
        hour12: true
      })
    : '';

/**
 *
 * @param {number} epochTime| this function returns the epoch in the format 'Month Day'. eg. January 2
 */
export const getFormattedDayAndMonth = (epochTime: numeric) =>
  epochTime
    ? new Date(epochTime).toLocaleString('en-US', {
        day: 'numeric',
        month: 'long'
      })
    : '';

/**
 *
 * @param {number} epochTime| this function returns the epoch in the format 'Month Year'. eg. January 2020
 */
export const getFormattedMonthAndYear = (epochTime: numeric) =>
  epochTime
    ? new Date(epochTime).toLocaleString('en-US', {
        year: 'numeric',
        month: 'long'
      })
    : '';

/**
 *
 * @param {number} epochTime| this function returns (input date + 1)th date
 */
export const getTomorrowDateEpoch = (epochTime: numeric) => {
  const tomorrow = new Date(epochTime);
  tomorrow.setDate(tomorrow.getDate() + 1);
  return tomorrow.getTime();
};

export const getFormattedDateAndTime = (epochTime: numeric) =>
  epochTime
    ? `${getFormattedDate(epochTime)}, ${getFormattedTime(epochTime)}`
    : '';

export const getRetailTableCellObject = (
  dataUp: string,
  dataDown: string,
  rowData: Object,
  clickHandler1: Function,
  customClassUp,
  customClassDown
) => (
  <div className="flex-100">
    <div
      className={`${
        // eslint-disable-next-line
        customClassUp ? customClassUp : ''
      } text-wrap-ellipsis ${isIE() ? 'overflow--visible ' : ''}`}
      title={dataUp}
      onClick={() =>
        clickHandler1 &&
        typeof clickHandler1 === 'function' &&
        clickHandler1(rowData)
      }
    >
      {dataUp || '--'}
    </div>
    <div
      className={`${
        // eslint-disable-next-line
        customClassDown ? customClassDown : ''
      } text-wrap-ellipsis ${isIE() ? 'overflow--visible ' : ''}`}
      title={dataDown}
    >
      {dataDown || ''}
    </div>
  </div>
);

export const nth = n =>
  ['st', 'nd', 'rd'][((((n + 90) % 100) - 10) % 10) - 1] || 'th';

export const getRetailTableFailureReasonCellObject = failureReasons => (
  <div className="flex-100">
    {failureReasons && failureReasons.length ? (
      failureReasons.map((reason, index) => {
        return (
          <div key={index} style={{ marginBottom: '5px' }}>
            <div className="text-wrap-ellipsis">
              Pickup- {index + 1 + nth(index + 1)} attempt
            </div>
            <div className="text-wrap-ellipsis">{reason}</div>
          </div>
        );
      })
    ) : (
      <div>--</div>
    )}
  </div>
);

export const getUserDetailTableCellObject = (
  name: string,
  mobile: string,
  email: string,
  rowData: Object,
  clickHandler1: Function,
  customClassForName,
  customClassForMobile,
  customClassForEmail
) => (
  <div className="flex-100">
    <div
      className={`${
        // eslint-disable-next-line
        customClassForName ? customClassForName : ''
      } text-wrap-ellipsis`}
      title={name}
      onClick={() =>
        clickHandler1 &&
        typeof clickHandler1 === 'function' &&
        clickHandler1(rowData)
      }
    >
      {name || '--'}
    </div>
    <div
      className={`${
        // eslint-disable-next-line
        customClassForMobile ? customClassForMobile : ''
      } text-wrap-ellipsis`}
      title={mobile}
    >
      {mobile || ''}
    </div>
    <div
      className={`${
        // eslint-disable-next-line
        customClassForEmail ? customClassForEmail : ''
      } text-wrap-ellipsis`}
      title={email}
    >
      {email || ''}
    </div>
  </div>
);

export const indianCurrency = (amount: String) => <div>&#8377;{amount}</div>;

export const getCollectionsHandoverTableCellObject = (
  cashAmount,
  chequeAmount,
  onlineAmount,
  writeOffAmount,
  securityDepositAmount,
  bankTransferAmount
) => (
  <div>
    {cashAmount ? (
      <div className="flex-20">Cash(&#8377;{cashAmount})</div>
    ) : null}
    {chequeAmount ? (
      <div className="flex-20">Cheque(&#8377;{chequeAmount})</div>
    ) : null}
    {onlineAmount ? (
      <div className="flex-20">Online(&#8377;{onlineAmount})</div>
    ) : null}
    {writeOffAmount ? (
      <div className="flex-20">Write-off(&#8377;{writeOffAmount})</div>
    ) : null}
    {securityDepositAmount ? (
      <div className="flex-20">
        Security Deposit(&#8377;
        {securityDepositAmount})
      </div>
    ) : null}
    {bankTransferAmount ? (
      <div className="flex-20">
        Bank Transfer(&#8377;
        {bankTransferAmount})
      </div>
    ) : null}
  </div>
);

export const getNameNumberObject = (obj, nameAccessor, numberAccessor) =>
  obj[nameAccessor] +
  strUsables.emptySpace +
  strUsables.openBracket +
  obj[numberAccessor] +
  strUsables.closeBracket;

export const getNameNumberList = (list, nameAccessor, numberAccessor) => {
  const toReturn = [];
  if (!list) {
    return toReturn;
  }
  if (!list.length) {
    return toReturn;
  }
  list.map(obj => {
    toReturn.push(getNameNumberObject(obj, nameAccessor, numberAccessor));
    return obj; // Returning only for es-lint
  });
  return toReturn;
};

export const getPaymentTypeCell = (type, bank, cheque) => {
  let toReturn = type;
  const typeLowerCase = type && type.toLowerCase();
  if (typeLowerCase === 'cash') {
    return toReturn;
  }
  if (typeLowerCase === 'cheque') {
    toReturn = `Cheque | ${bank}`;
    return getRetailTableCellObject(toReturn, cheque);
  }
  return type;
};

export const formattedList = (list, separatorStr) => {
  let separator = ', ';
  if (separatorStr) {
    separator = separatorStr;
  }
  return list.join(separator);
};

export const isAuthorized = (userProfile: Object, route: Object) => {
  const { roles } = route;
  let isAuthorizedVar = false;
  if (roles && roles.length) {
    roles.forEach(role => {
      if (
        userProfile &&
        userProfile.authorities &&
        userProfile.authorities.indexOf(role) !== -1
      ) {
        isAuthorizedVar = true;
      }
    });
    if (userProfile && route.locationAllowed) {
      isAuthorizedVar =
        route.locationAllowed.indexOf(userProfile.location_id) !== -1;
    }
  } else {
    // No role authentication present
    isAuthorizedVar = true;
  }
  return isAuthorizedVar;
};

export const checkAuthentication = (routeObj: Object, userProfile: Object) => {
  let isAuthorizedVar = false;
  if (typeof routeObj.isAuthorized === 'function') {
    isAuthorizedVar = routeObj.isAuthorized(userProfile, routeObj);
  } else {
    isAuthorizedVar = isAuthorized(userProfile, routeObj);
  }

  if (isRelianceConsignorOrAdmin(userProfile)) {
    if (
      routeObj.title !== 'PENDING PICKUPS' &&
      routeObj.title !== 'INCOMPLETE CNS' &&
      routeObj.title !== 'ASN DETAILS' &&
      routeObj.title !== 'TRACK CN' &&
      routeObj.title !== 'CN DASHBOARD' &&
      routeObj.title !== 'BULK CONSIGNMENT TRACKING' &&
      routeObj.title !== 'TRACK PICKUP' &&
      routeObj.title !== 'PICKUP DASHBOARD' &&
      routeObj.title !== 'TRACK PICKUP PARAMS' &&
      routeObj.title !== 'BOOK ORDER'
    ) {
      return null;
    }
  }

  if (isRelianceConsignor(userProfile)) {
    if (routeObj.title === 'BOOK ORDER') {
      return null;
    }
  }

  if (
    userProfile &&
    isAuthorized &&
    routeObj &&
    openUrls.includes(routeObj.route)
  ) {
    isAuthorizedVar = true;
  }

  if (
    isAuthorizedVar &&
    routeObj &&
    (routeObj.route === '/collections-handover' ||
      routeObj.route === '/retail-collections' ||
      routeObj.route === '/retail-cashbook')
  ) {
    isAuthorizedVar = userProfile.cashBookV2Allowed;
  }
  if (isAuthorizedVar && routeObj && routeObj.route === '/bppassbook') {
    isAuthorizedVar = userProfile.bpWalletEnabled;
  }

  if (isAuthorizedVar && routeObj && routeObj.showToTech) {
    if (routeObj.route === '/manage-alerts') {
      isAuthorizedVar =
        userProfile &&
        userProfile.authorities &&
        userProfile.authorities.includes('ROLE_VIEW_CLIENT_COMMUNICATION') &&
        !isClient(userProfile);
    } else if (routeObj.route === '/prqdashboard') {
      if (
        userProfile &&
        userProfile.authorities &&
        userProfile.authorities.includes('ROLE_RELIANCE_ADMIN_USER')
      ) {
        isAuthorizedVar = isAuthorizedVar;
      } else {
        isAuthorizedVar =
          userProfile &&
          userProfile.authorities &&
          (userProfile.authorities.includes(
            'ROLE_LOGICET_CLIENT_PICKUP_DASHBOARD'
          ) ||
            userProfile.authorities.includes('ROLE_PRQ_DASHBOARD')) &&
          !isClient(userProfile);
      }
    } else {
      isAuthorizedVar = !isClient(userProfile);
    }
  }

  if (
    routeObj.route === '/bulkPincodeCheck' &&
    userProfile &&
    userProfile.authorities.includes('ROLE_ANONYMOUS_USER')
  ) {
    return null;
  }

  if (routeObj.route === '/rp-collections') {
    if (
      userProfile &&
      userProfile.sessionProperties &&
      userProfile.sessionProperties.RP_WALLET_ENABLED_LOCATIONS &&
      userProfile.sessionProperties.RP_WALLET_ENABLED_LOCATIONS.length > 0
    ) {
      let locationAllowed = userProfile.sessionProperties.RP_WALLET_ENABLED_LOCATIONS.split(
        ','
      ).map(location => location.trim());

      if (
        locationAllowed &&
        locationAllowed.length > 0 &&
        locationAllowed.indexOf(userProfile.location_code) === -1
      ) {
        isAuthorizedVar = false;
      }
    }
  }

  if (routeObj.route == '/incompletecnote') {
    if (isRelianceConsignorOrAdmin(userProfile)) {
      routeObj.title = 'ASN DETAILS';
    }
  }

  return isAuthorizedVar ? routeObj : null;
};

export const checkAuthenticationForSpecificRole = (
  roleToBeChecked: String,
  userProfile: Object
) => userProfile && userProfile.authorities.indexOf(roleToBeChecked) !== -1;

export const getRouteObjFromPath = (
  pathStr: string,
  routesConfig: Array<any>
) => {
  let matchedRouteObj = {};
  routesConfig.filter(routeObj => {
    if (routeObj.subRoutes) {
      const subRouteEntered = routeObj.subRoutes.filter(
        subRouteObj => subRouteObj.route.indexOf(pathStr) >= 0
      );
      if (subRouteEntered.length) {
        [matchedRouteObj] = subRouteEntered;
        return true;
      }
      return false;
    }

    if (routeObj.route.indexOf(pathStr) >= 0) {
      matchedRouteObj = routeObj;
      return true;
    }
    return false;
  });
  return matchedRouteObj;
};

export const checkUserAccessOnRoute = (
  pathStr: string,
  userProfile: Object,
  routesConfig: Array<any>
) => {
  const matchedRouteObj = getRouteObjFromPath(pathStr, routesConfig);
  const isAuthorizedVar = checkAuthentication(matchedRouteObj, userProfile);
  if (isAuthorizedVar) {
    return true;
  }
  return false;
};

export const getUniqueId = (length: number) => {
  const randomNo = Math.random();
  const digits = 10 ** length;
  return parseInt(randomNo * digits, 10);
};

export const shouldTableDataBeFetchedForNewDateRange = (
  nextDateFilterValues,
  presentDateFilterValues
) =>
  nextDateFilterValues &&
  presentDateFilterValues &&
  nextDateFilterValues.startDate &&
  nextDateFilterValues.endDate &&
  (nextDateFilterValues.startDate !== presentDateFilterValues.startDate ||
    nextDateFilterValues.endDate !== presentDateFilterValues.endDate);

export const shouldTableDataBeFetchedForLocationChange = (
  nextDateFilterValues,
  presentDateFilterValues
) =>
  nextDateFilterValues &&
  presentDateFilterValues &&
  nextDateFilterValues.locationId !== presentDateFilterValues.locationId;

export const downloadArrayBufferAsXLS = (
  data,
  fileName,
  downloadAsXls = false
) => {
  let type =
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  if (downloadAsXls) {
    type = 'application/vnd.ms-excel';
  }
  const blob = new Blob([data], {
    type
  });
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.download = fileName;
  link.click();
};

export const goToCNTracking = (rowData: Object, openUrlInSameTab: Boolean) => {
  if (!rowData.cnote) {
    return;
  }
  const option = openUrlInSameTab ? '_self' : '_blank';
  const win = window.open(
    `${config.oldDomainUrl}trackconsignment/${rowData.cnote}`,
    option
  );
  win.focus();
};

export const trackPickup = (rowData: Object) => {
  if (!rowData.id && !rowData.pickupId) {
    return;
  }
  const win = window.open(
    `${config.baseName}${zoomConstants.trackPickup}${rowData.id}`,
    '_blank'
  );
  win.focus();
};

export const trackCN = (rowData: Object) => {
  if (!rowData.cnote) {
    return;
  }
  let redirectedFromRivigo = false;
  if (window.location.search.includes('redirectedFromRivigo=true')) {
    redirectedFromRivigo = true;
  }
  const win = window.open(
    `${config.oldDomainUrl}${zoomConstants.tracking}${rowData.cnote}${
      redirectedFromRivigo ? `?redirectedFromRivigo=true` : ``
    }`,
    '_blank'
  );
  win.focus();
};
export const createCn = () => {
  const win = window.open(
    `${config.oldDomainUrl}${IncompleteCnConstants.createCn}`,
    '_self'
  );
  win.focus();
};

export const completeCn = (rowData: Object) => {
  if (!rowData.original.cnote) {
    return;
  }
  const win = window.open(
    `${config.oldDomainUrl}${IncompleteCnConstants.createCn}?cnote=${rowData.original.cnote}&redirectedFrom=INCOMPLETE`,
    '_self'
  );
  win.focus();
};

export const completeCnReliance = (rowData: Object) => {
  if (!rowData.original.cnote) {
    return;
  }
  const win = window.open(
    `${config.oldDomainUrl}${IncompleteCnConstants.createCn}?cnote=${rowData.original.cnote}&redirectedFrom=INCOMPLETE&asnNumber=${rowData.original.clientAsnNumber}`,
    '_self'
  );
  win.focus();
};

export const isBulkCnValid = val => {
  return /^[\d]{10}(\-[\d]{1,10}){0,1}([,]+[\d]{10}(\-[\d]{1,10}){0,1})*[,]*$/.test(
    val
  );
};

export const getPaymentTypeString = (mode: string) => {
  let paymentType = '';

  if (mode === 'TO_PAY') {
    paymentType = 'TO PAY';
  } else if (mode === 'PAID') {
    paymentType = 'PAID';
  }
  return paymentType;
};

export const sendGaEvent = (
  eventObj: Object,
  action: string,
  userProfile: Object
) => {
  // eslint-disable-next-line no-console
  console.log('GA event object {} ', eventObj);
  if (window.gtag) {
    window.gtag(
      'event',
      action,
      Object.assign(eventObj, {
        userId: userProfile && userProfile.email,
        userLocation: userProfile && userProfile.location_code,
        userOrg: userProfile && userProfile.organizationId
      })
    );
  }
};

export const navigateToRouteHelper = (routeObj: Object, newWindow: Boolean) => {
  if (routeObj.route) {
    if (routeObj.previousReactRoute) {
      window.location = config.oldReactDomainUrl + routeObj.route;
      // eslint-disable-next-line no-console
      console.log(' navigate to old react route: ', routeObj.route);
    } else if (routeObj.previousRoute) {
      if (newWindow) {
        window.open(`${config.oldDomainUrl}${routeObj.route}`, '_blank');
      } else {
        window.location = config.oldDomainUrl + routeObj.route;
      }
      // eslint-disable-next-line no-console
      console.log(" navigate to old route: '/#/'", routeObj.route);
    } else if (routeObj.routedeveloper) {
      window.location = `${config.developerUrl}`;
    } else if (routeObj.isExternalRoute) {
      window.location = `${config[routeObj.route]}`;
    } else {
      history.push(`${routeObj.route}`);
      // eslint-disable-next-line no-console
      console.log(" navigate to route: '/'", routeObj.route);
    }
  }
};

export const validateMobileNumber = mobilenumber => {
  const validregex = /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;
  return mobilenumber && mobilenumber.match(validregex);
};

export const validateEmail = email => {
  // eslint-disable-next-line no-useless-escape
  const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(email);
};

export const enableOnlyNumberInput = (event, input: String, maxInputLength) => {
  if (
    event.which < 48 ||
    event.which > 57 ||
    (maxInputLength && input && input.length === maxInputLength)
  ) {
    event.preventDefault();
  }
};

export const isValidEmail = email => {
  // eslint-disable-next-line
  const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

export const validTrackingSearch = value => {
  const re = /^[a-zA-Z0-9, ]*$/g;
  return re.test(value);
};

export const isNullOrEmptyString = inputString =>
  !inputString || inputString === '';

export const isValidJson = obj => {
  let parsed;
  try {
    parsed = JSON.parse(obj);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log('Not a valid JSON ', obj);
    return false;
  }
  return parsed;
};

export const findObjectByAccessor = (
  accessor: string,
  value: any,
  list: Array<any>
) => {
  if (!isArrayNonEmpty(list)) {
    return null;
  }
  let toreturn;
  list.map(obj => {
    if (obj[accessor] === value) {
      toreturn = obj;
    }
    return obj; // for eslint
  });
  return toreturn;
};

export const performFormValiations = (componentDict, stateInstance) => {
  let blockCallFlag = false;
  // Should be useful in all forms. This method will update the same componentDict object
  Object.keys(componentDict).map(compKey => {
    const compObject = componentDict[compKey];
    compObject.requiredFailed = false; // so that fresh validations are run everytime
    compObject.validationFailed = false;
    compObject.errorMessage = null;
    // beginning the validations
    const accessor = compObject.stateAccessor;
    if (typeof compObject.required === 'boolean' && compObject.required) {
      // stateInstance should have the accessor value
      if (!stateInstance[accessor]) {
        compObject.requiredFailed = true;
        compObject.errorMessage = compObject.requiredFailedMessage;
        blockCallFlag = true;
      } else if (
        stateInstance[accessor] &&
        typeof stateInstance[accessor] !== typeof compObject.type &&
        !compObject.requiredNestedKey
      ) {
        compObject.requiredFailed = true;
        compObject.errorMessage = compObject.requiredFailedMessage;
        blockCallFlag = true;
      } else if (typeof compObject.requiredNestedKey === 'string') {
        if (!stateInstance[accessor][compObject.requiredNestedKey]) {
          compObject.requiredFailed = true;
          compObject.errorMessage = compObject.requiredFailedMessage;
          blockCallFlag = true;
        }
      }
    }
    if (
      !compObject.errorMessage &&
      compObject.validationFunction &&
      typeof compObject.validationFunction === 'function'
    ) {
      const returned = compObject.validationFunction(
        stateInstance[accessor],
        stateInstance
      );
      if (returned === false) {
        compObject.validationFailed = true;
        compObject.errorMessage = compObject.validationFailedMessage;
        blockCallFlag = true;
      } else if (typeof returned === 'string' && returned.length) {
        compObject.validationFailed = true;
        compObject.errorMessage = returned;
        blockCallFlag = true;
      }
    }
    return compKey; // for eslint
  });
  return blockCallFlag;
};

export const performErrorMessageValidations = (
  componentDict,
  stateInstance
) => {
  const errorObjectToReturn = {};

  // Should be useful in all forms. This method will update the same componentDict object
  Object.keys(componentDict).forEach(compKey => {
    const compObject = componentDict[compKey];
    // beginning the validations
    const accessor = compObject.stateAccessor;
    // stateInstance should have the accessor value
    if (compObject.required && !stateInstance[accessor]) {
      errorObjectToReturn[accessor] = compObject.requiredFailedMessage;
    } else if (
      compObject.required &&
      compObject.validationFun &&
      typeof compObject.validationFun === 'function' &&
      !compObject.validationFun(stateInstance[accessor])
    ) {
      errorObjectToReturn[accessor] = compObject.validationFailedMessage;
    }
  });
  return errorObjectToReturn;
};

export const getNestedValue = (nestedObjects, row) => {
  let val;
  for (let i = 0; i < nestedObjects.length; i += 1) {
    const key = nestedObjects[i];
    if (i === 0 && row[key]) {
      val = row[key];
    } else if (val && val[key]) {
      val = val[key];
    } else {
      val = null;
      break;
    }
  }
  return val;
};

const columnDownloadNested = (downloadableValue, data) => {
  const nestedAccessor = downloadableValue
    ? downloadableValue.split('.')
    : null;

  return nestedAccessor ? getNestedValue(nestedAccessor, data) : null;
};

export const downloadCSV = (csvData, fileName) => {
  const csvBlob = new Blob([csvData], { type: 'text/csv' });
  const uri = URL.createObjectURL(csvBlob);
  const link = document.createElement('a');
  link.href = uri;
  link.style.visibility = 'hidden';
  link.download = `${fileName}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const createCSV = (
  JSONData,
  fileName,
  columns,
  doNotAddSpaceAfterCellValue = false
) => {
  const tableData =
    typeof JSONData !== 'object' ? JSON.parse(JSONData) : JSONData;
  let CSV = '';
  let row = '';

  if (columns) {
    columns.forEach(column => {
      if (column.Header !== 'Action') {
        const headerValue = column.Header;
        row += `${headerValue},`;
      }
    });
  }
  row = row.slice(0, -1);
  CSV += `${row}\r\n`;

  tableData.forEach(data => {
    row = '';
    columns.forEach(header => {
      let val = '';
      let cellValue = '';
      if (typeof header.formatter === 'function') {
        val = header.formatter(data);
      } else if (header.key) {
        val = columnDownloadNested(header.key, data);
      } else if (header.accessor) {
        val = columnDownloadNested(header.accessor, data);
      }
      if (doNotAddSpaceAfterCellValue) {
        cellValue = val ? `${cellValue}${val}` : ' ';
      } else {
        cellValue = val ? `${cellValue}${val} ` : ' ';
      }
      row += `"${cellValue}",`;
    });
    CSV += `${row}\r\n`;
  });

  if (!CSV) {
    return '';
  }
  return CSV;
};

export const jsonToCSVConvertor = (
  JSONData,
  fileName,
  columns,
  doNotAddSpaceAfterCellValue = false
) => {
  const CSV = createCSV(
    JSONData,
    fileName,
    columns,
    doNotAddSpaceAfterCellValue
  );
  downloadCSV(CSV, fileName);
};

export const getTimeinHoursWithMinutes = time => {
  time = Math.floor(time / 60000);
  let days = 0;
  const minutes = Math.floor(time % 60);
  time = Math.floor(time / 60);
  if (time > 24) {
    days = Math.floor(time / 24);
  }
  const hrs = Math.floor(time % 24);
  let timeStr = '';
  if (days) {
    timeStr += days + (days > 1 ? ' days, ' : ' day, ');
  }
  timeStr += hrs + (hrs > 1 ? ' hrs, ' : ' hr, ');
  timeStr += minutes + (minutes > 1 ? ' minutes' : ' minute');
  return timeStr;
};

export const millisOfHalfDay = 43200000;

export const minimumClientSearchCharacterLength = 2;

export const clientSearchPlaceholders = {
  noClients: 'No Clients Found',
  selectClient: 'Please select a client',
  default: 'Search Clients (Min 2 characters)',
  searchingClients: 'Searching Clients....'
};

export const getClientSearchPlaceholder = (
  clientSearchInput,
  fetchingClients,
  clientList
) => {
  if (clientSearchInput && !fetchingClients) {
    if (!clientList || !clientList.length) {
      return clientSearchPlaceholders.noClients;
    }
    return clientSearchPlaceholders.selectClient;
  }
  if (clientSearchInput && fetchingClients) {
    return clientSearchPlaceholders.searchingClients;
  }
  return clientSearchPlaceholders.default;
};

export const removeUnderScore = (str: string) =>
  str ? str.replace(/_/gi, ' ') : '--';

export const getCookieDomain = () =>
  window.location.hostname.slice(window.location.hostname.indexOf('.'));

export const resetCookieAndSession = () => {
  const cookieDomain = getCookieDomain(); // .stg.rivigo.com
  let parentDomain = cookieDomain && cookieDomain.slice(1);
  parentDomain = cookieDomain && cookieDomain.slice(cookieDomain.indexOf('.')); // .rivigo.com
  document.cookie = `${strUsables.accessToken}=; domain=${cookieDomain}; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
  document.cookie = `${strUsables.accessToken}=; domain=${parentDomain}; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
  sessionStorage.removeItem(strUsables.accessToken);
};

export const setCookieAndSession = token => {
  if (token) {
    const expiryDate = new Date();
    expiryDate.setTime(expiryDate.getTime() + 2592000000); // Millis in 30 days
    document.cookie = `${
      strUsables.accessToken
    }=${token}; domain=${getCookieDomain()}; expires=${expiryDate.toUTCString()}; path=/`;
    sessionStorage.setItem(strUsables.accessToken, token);
  }
};

export const getCookie = (cname: string) => {
  const name = `${cname}=`;
  const decodedCookie = decodeURIComponent(document.cookie);
  const ca = decodedCookie.split(';');
  for (let i = 0; i < ca.length; i += 1) {
    let c = ca[i];
    while (c.charAt(0) === ' ') {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }
  return '';
};

export const setAuthHeader = token => {
  return strUsables.bearerString + strUsables.emptySpace + token;
};

export const redirectToPath = url => {
  if (url) {
    window.location.href = url;
  }
};

export const isBearerTokenPresent = () => {
  const bearerToken = sessionStorage.getItem(strUsables.accessToken);
  return bearerToken && bearerToken.length > 0;
};

export const getDeepCopyOfVariable = (obj: Object) =>
  JSON.parse(JSON.stringify(obj));

export const isPincodeValid = (val: string) => val && val.length === 6;

export const isPincodeNumber = val => /^\d+$/.test(val);

export const isGstinValid = (val: string) =>
  val &&
  val.length === 15 &&
  val.match('[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}');

export const isPanNumberValid = (val: string) =>
  val && val.length === 10 && val.match('([a-zA-Z]){5}([0-9]){4}([a-zA-Z]){1}');

export const isTanNumberValid = (val: string) =>
  val && val.length === 10 && val.match('^[A-Za-z]{4}[0-9]{5}[A-Za-z]{1}$');

export const isValidMobileNumber = (val: string) =>
  val && val.length === 10 && val.match('[6-9][0-9]{9}');

export const handleNumberInput = (value: string, limitDigits: number) => {
  if (Number(value) < 0) {
    return '';
  }

  let length = value.toString().length;
  if (
    (limitDigits && length > limitDigits) ||
    isNaN(parseInt(value[length - 1]))
  ) {
    return value.toString().substr(0, length - 1);
  }
  return value;
};
export const debounce = (func, wait) => {
  let timeout;
  return function() {
    const context = this,
      args = arguments;
    const later = function() {
      timeout = null;
      func.apply(context, args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

export const toKeyValPair = (inputArray, appendExtraKey, appendExtraVal) => {
  return inputArray.map(item => {
    if (appendExtraKey) {
      return {
        key: item,
        value: item,
        [appendExtraKey]: appendExtraVal
      };
    } else {
      return {
        key: item,
        value: item
      };
    }
  });
};

export const isValidUtrNo = utrNo => {
  if (!utrNo || !(utrNo.length >= 6 && utrNo.length <= 24)) {
    return false;
  }
  return true;
};

export const downloadPdf = (data, fileName) => {
  let file = new Blob([data], { type: 'application/pdf;base64' });
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(file);
  link.download = fileName;
  link.click();
};

export const isIE = () => {
  const ua = navigator.userAgent;
  /* MSIE used to detect old browsers and Trident used to newer ones*/
  const is_ie = ua.indexOf('MSIE ') > -1 || ua.indexOf('Trident/') > -1;

  return is_ie;
};

/**
 * isBpUser: To check if the current user is a BP
 *
 * @param userProfile
 * @returns {*|boolean}
 */
export const isBpUser = userProfile =>
  userProfile.authorities &&
  (userProfile.authorities.indexOf('ROLE_ZOOM_BP') > -1 ||
    userProfile.authorities.indexOf('ROLE_ZOOM_BP_USER') > -1);

/**
 * isBfUser: To check if the current user is a BF
 * @param userProfile
 * @returns {*|boolean}
 */

export const isBFUser = userProfile =>
  userProfile &&
  userProfile.organizationType &&
  userProfile.organizationType === 'BF';

/**
 * isClientUser: To check if the current user has Access to Client Performance Dashboard Filter selection
 * @param userProfile
 * @returns {*|boolean}
 */
export const checkEnableServicePerformanceFilter = userProfile => {
  return (
    userProfile.authorities &&
    userProfile.authorities.indexOf('ROLE_PERFORMANCE_DASHBOARD_FILTERS') > -1
  );
};

export const checkPerformanceDashboardSalesViewRole = userProfile => {
  return (
    userProfile.authorities &&
    userProfile.authorities.indexOf('ROLE_PERFORMANCE_DASHBOARD_SALES_VIEW') >
      -1
  );
};

/**
 * fixAndOperatorforQueryParams: To fix the query params for the API call for the & operator
 * Replace the & with %26
 * @param value: string
 * @returns valueUpdated: string with %26
 *
 */

export const fixAndOperatorforQueryParams = value => {
  let valueUpdated = value;
  if (valueUpdated) {
    valueUpdated = valueUpdated.replace('&', '%26');
  }
  return valueUpdated;
};

/**
 *
 * @param {*} response
 * @returns file name extracted from content-disposition header
 */
export const getFileNameFromContentDisposition = response => {
  let required_fileName = 'Request file';
  const contentDisposition = response.headers.get('content-disposition');
  if (contentDisposition) {
    const contentSispositionString = contentDisposition.split(';');
    if (contentSispositionString.length > 1) {
      const fileNameString = contentSispositionString[1].split('=');
      if (fileNameString.length > 1) {
        const fileName = fileNameString[1].split('"');
        if (fileName.length > 1) {
          required_fileName = fileName[1];
        }
      }
    }
  }
  return required_fileName;
};

/**
 *
 * @returns return true if loggedIn user is Rp Admin or Bp Admin
 */
export const isRpAdmin = userProfile => {
  return (
    (userProfile.authorities &&
      userProfile.authorities.indexOf('ROLE_ZOOM_FINANCE_BP_COLLECTION') >
        -1) ||
    (userProfile.authorities &&
      userProfile.authorities.indexOf('ROLE_ZOOM_FINANCE_RP_COLLECTION') > -1)
  );
};

export const isZoomFinanceManager = userProfile => {
  return (
    userProfile.authorities &&
    userProfile.authorities.indexOf('ROLE_ZOOM_FINANCE_MANAGER') > -1
  );
};

export const isRelianceConsignor = userProfile => {
  return (
    userProfile.authorities &&
    userProfile.authorities.indexOf('ROLE_RELIANCE_CONSIGNOR_USER') > -1
  );
};

export const isRelianceAdmin = userProfile => {
  return (
    userProfile.authorities &&
    userProfile.authorities.indexOf('ROLE_RELIANCE_ADMIN_USER') > -1
  );
};

export const isRelianceConsignorOrAdmin = userProfile => {
  return (
    userProfile.authorities &&
    (userProfile.authorities.indexOf('ROLE_RELIANCE_CONSIGNOR_USER') > -1 ||
      userProfile.authorities.indexOf('ROLE_RELIANCE_ADMIN_USER') > -1)
  );
};

export const getRelianceRetailClientCodes = userProfile => {
  let relianceRetailClientCodesArray = [];
  if (
    userProfile.sessionProperties &&
    userProfile.sessionProperties.RELIANCE_RETAIL_CLIENT_CODES
  ) {
    relianceRetailClientCodesArray = userProfile.sessionProperties.RELIANCE_RETAIL_CLIENT_CODES.split(
      ','
    );
  }
  return relianceRetailClientCodesArray;
};

export const isRelianceClientode = (userProfile, clientCode) => {
  const relianceRetailClientCodesArray = getRelianceRetailClientCodes(
    userProfile
  );
  return (relianceRetailClientCodesArray || []).indexOf(clientCode) > -1;
};
